import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Country, State } from 'country-state-city';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { agenciesAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import SearchableDropdown from '../components/SearchableDropdown';
import Select from '../components/Select';
import './AgencyForm.css';

const statusOptions = [
  { value: 'pending', label: 'Pending', name: 'status' },
  { value: 'approved', label: 'Approved', name: 'status' },
  { value: 'rejected', label: 'Rejected', name: 'status' },
  { value: 'suspended', label: 'Suspended', name: 'status' }
];

const AgencyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [formData, setFormData] = useState({
    agency_name: '',
    email: '',
    phone_country_code: '',
    phone_number: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state_province: '',
    country: '',
    country_code: '',
    postal_code: '',
    tax_identification_number: '',
    business_registration_number: '',
    status: 'pending'
  });

  // Get all countries
  const countries = useMemo(() => {
    return Country.getAllCountries().map(country => ({
      name: country.name,
      value: country.isoCode,
      phonecode: country.phonecode,
      flag: country.flag
    }));
  }, []);

  // Get states based on selected country
  const states = useMemo(() => {
    if (!formData.country_code) return [];
    return State.getStatesOfCountry(formData.country_code).map(state => ({
      name: state.name,
      value: state.isoCode
    }));
  }, [formData.country_code]);

  // Phone country codes with flags
  const phoneCountryCodes = useMemo(() => {
    return Country.getAllCountries().map(country => {
      let callingCode = country.phonecode;
      // Ensure + prefix
      if (!callingCode.startsWith('+')) {
        callingCode = '+' + callingCode;
      }
      return {
        name: country.name,
        value: callingCode,
        code: country.isoCode,
        flag: country.flag
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchAgency();
    }
  }, [id]);

  const fetchAgency = async () => {
    try {
      setLoading(true);
      const response = await agenciesAPI.getById(id);
      if (response.data.success) {
        const agency = response.data.data.agency;
        setFormData({
          agency_name: agency.agency_name || '',
          email: agency.email || '',
          phone_country_code: agency.phone_country_code || '',
          phone_number: agency.phone_number || '',
          address_line_1: agency.address_line_1 || '',
          address_line_2: agency.address_line_2 || '',
          city: agency.city || '',
          state_province: agency.state_province || '',
          country: agency.country || '',
          country_code: agency.country_code || '',
          postal_code: agency.postal_code || '',
          tax_identification_number: agency.tax_identification_number || '',
          business_registration_number: agency.business_registration_number || '',
          status: agency.status || 'pending'
        });
      }
    } catch (error) {
      console.error('Failed to fetch agency:', error);
      setError('Failed to load agency details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleCountryChange = (value, option) => {
    setFormData(prev => ({
      ...prev,
      country_code: value,
      country: option.name,
      state_province: '' // Reset state when country changes
    }));
    setError('');
  };

  const handleStateChange = (value, option) => {
    setFormData(prev => ({
      ...prev,
      state_province: option.name
    }));
    setError('');
  };

  const handlePhoneCodeChange = (value, option) => {
    setFormData(prev => ({
      ...prev,
      phone_country_code: value
    }));
    setPhoneError('');
    // Re-validate phone number with new country code
    if (formData.phone_number) {
      validatePhoneNumber(formData.phone_number, option.code);
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, phone_number: value }));

    // Find country code from selected phone code
    const phoneOption = phoneCountryCodes.find(p => p.value === formData.phone_country_code);
    if (phoneOption && value) {
      validatePhoneNumber(value, phoneOption.code);
    } else {
      setPhoneError('');
    }
  };

  const validatePhoneNumber = (number, countryCode) => {
    if (!number || !countryCode) {
      setPhoneError('');
      return true;
    }

    try {
      const fullNumber = formData.phone_country_code + number;
      if (isValidPhoneNumber(fullNumber, countryCode)) {
        setPhoneError('');
        return true;
      } else {
        setPhoneError(`Invalid phone number for ${countryCode}`);
        return false;
      }
    } catch (err) {
      setPhoneError('Invalid phone number format');
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.agency_name.trim()) {
      setError('Agency name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // Validate phone if provided
    if (formData.phone_number && formData.phone_country_code) {
      const phoneOption = phoneCountryCodes.find(p => p.value === formData.phone_country_code);
      if (phoneOption && !validatePhoneNumber(formData.phone_number, phoneOption.code)) {
        setError('Please fix the phone number error');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError('');

      if (isEditMode) {
        await agenciesAPI.update(id, formData);
      } else {
        await agenciesAPI.create(formData);
      }

      navigate('/agencies');
    } catch (error) {
      console.error('Failed to save agency:', error);
      setError(error.response?.data?.message || 'Failed to save agency');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this agency?')) {
      return;
    }

    try {
      setSubmitting(true);
      await agenciesAPI.delete(id);
      navigate('/agencies');
    } catch (error) {
      console.error('Failed to delete agency:', error);
      setError('Failed to delete agency');
    } finally {
      setSubmitting(false);
    }
  };

  // Render phone option - just show the code
  const renderPhoneOption = (option, isSelected) => (
    <span className="phone-code-value">{option.value}</span>
  );

  // Render country option with flag
  const renderCountryOption = (option, isSelected) => (
    <div className="country-option">
      <span className="flag">{option.flag}</span>
      <span className="country-name">{option.name}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="dashboard-main">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading agency...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/agencies')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5"></path>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              BACK
            </button>
            <h1 className="page-title">{isEditMode ? 'EDIT AGENCY' : 'NEW AGENCY'}</h1>
            <p className="page-subtitle">{isEditMode ? 'UPDATE AGENCY DETAILS' : 'CREATE NEW TRAVEL AGENCY'}</p>
          </div>
          {isEditMode && (
            <div className="header-right">
              <button className="delete-btn" onClick={handleDelete} disabled={submitting}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                DELETE
              </button>
            </div>
          )}
        </div>

        <div className="dashboard-content">
          <form className="agency-form" onSubmit={handleSubmit}>
            {error && <div className="form-error">{error}</div>}

            <div className="form-section">
              <h3 className="form-section-title">BASIC INFORMATION</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Agency Name *</label>
                  <input
                    type="text"
                    name="agency_name"
                    className="form-input"
                    value={formData.agency_name}
                    onChange={handleChange}
                    placeholder="Enter agency name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div className="form-group form-group-phone">
                  <label className="form-label">Phone Number</label>
                  <div className="phone-input-group">
                    <div className="phone-code-dropdown">
                      <SearchableDropdown
                        options={phoneCountryCodes}
                        value={formData.phone_country_code}
                        onChange={handlePhoneCodeChange}
                        placeholder="+1"
                        searchPlaceholder="Search +91, India..."
                        displayKey="value"
                        valueKey="value"
                        searchKeys={['name', 'value', 'code']}
                        renderOption={renderPhoneOption}
                      />
                    </div>
                    <input
                      type="text"
                      name="phone_number"
                      className={`form-input phone-number ${phoneError ? 'input-error' : ''}`}
                      value={formData.phone_number}
                      onChange={handlePhoneNumberChange}
                      placeholder="Enter phone number"
                    />
                  </div>
                  {phoneError && <span className="field-error">{phoneError}</span>}
                </div>
                {isEditMode && (
                  <div className="form-group">
                    <Select
                      label="Status"
                      options={statusOptions}
                      value={formData.status}
                      onChange={handleChange}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">ADDRESS</h3>
              <div className="form-grid">
                <div className="form-group">
                  <SearchableDropdown
                    label="Country"
                    options={countries}
                    value={formData.country_code}
                    onChange={handleCountryChange}
                    placeholder="Select country"
                    searchPlaceholder="Search country..."
                    displayKey="name"
                    valueKey="value"
                    renderOption={renderCountryOption}
                  />
                </div>
                <div className="form-group">
                  <SearchableDropdown
                    label="State / Province"
                    options={states}
                    value={states.find(s => s.name === formData.state_province)?.value || ''}
                    onChange={handleStateChange}
                    placeholder={formData.country_code ? "Select state" : "Select country first"}
                    searchPlaceholder="Search state..."
                    displayKey="name"
                    valueKey="value"
                    disabled={!formData.country_code || states.length === 0}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    className="form-input"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter city name"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Postal Code</label>
                  <input
                    type="text"
                    name="postal_code"
                    className="form-input"
                    value={formData.postal_code}
                    onChange={handleChange}
                    placeholder="Postal / ZIP code"
                  />
                </div>
                <div className="form-group form-group-full">
                  <label className="form-label">Address Line 1</label>
                  <input
                    type="text"
                    name="address_line_1"
                    className="form-input"
                    value={formData.address_line_1}
                    onChange={handleChange}
                    placeholder="Street address"
                  />
                </div>
                <div className="form-group form-group-full">
                  <label className="form-label">Address Line 2</label>
                  <input
                    type="text"
                    name="address_line_2"
                    className="form-input"
                    value={formData.address_line_2}
                    onChange={handleChange}
                    placeholder="Apartment, suite, etc."
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">BUSINESS DETAILS</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Tax ID / VAT Number</label>
                  <input
                    type="text"
                    name="tax_identification_number"
                    className="form-input"
                    value={formData.tax_identification_number}
                    onChange={handleChange}
                    placeholder="Tax identification number"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Business Registration Number</label>
                  <input
                    type="text"
                    name="business_registration_number"
                    className="form-input"
                    value={formData.business_registration_number}
                    onChange={handleChange}
                    placeholder="Business registration number"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/agencies')}
                disabled={submitting}
              >
                CANCEL
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'SAVING...' : isEditMode ? 'UPDATE AGENCY' : 'CREATE AGENCY'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AgencyForm;
